import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  PackageSearchIcon, LogOutIcon, TruckIcon, PackageOpenIcon, MapPinIcon,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/* ================= PORTAL DO CLIENTE — READ-ONLY, ÁREA ISOLADA ================= */

const STATUS_CORES = {
  EM_TRANSITO: "bg-[#00E5FF]/10 text-[#00E5FF] border-[#00E5FF]/40",
  NO_CLIENTE: "bg-[#FFD60A]/10 text-[#FFD60A] border-[#FFD60A]/40",
  ENVASADO: "bg-[#FF9F0A]/10 text-[#FF9F0A] border-[#FF9F0A]/40",
  CONCLUIDO: "bg-[#34C759]/10 text-[#34C759] border-[#34C759]/40",
};
const STATUS_LABEL = { EM_TRANSITO: "EM TRÂNSITO", NO_CLIENTE: "NO DESTINO", ENVASADO: "PREPARANDO", CONCLUIDO: "DEVOLVIDO" };
const fmtD = (s) => (s ? new Date(s).toLocaleDateString("pt-BR") : "—");
const fmtDT = (s) => (s ? new Date(s).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

export default function ClientePortal() {
  const { user, logout } = useAuth();
  const [me, setMe] = useState(null);
  const [cargas, setCargas] = useState([]);
  const [devolvidas, setDevolvidas] = useState([]);
  const [aba, setAba] = useState("cargas");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [m, r] = await Promise.all([api.get("/cliente/me"), api.get("/cliente/rastreio")]);
      setMe(m.data);
      setCargas(r.data.cargas);
      setDevolvidas(r.data.devolvidas);
    } catch { /* sessão tratada pelo interceptor */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 120000); return () => clearInterval(t); }, []);

  const kpis = useMemo(() => ({
    transito: cargas.filter((c) => c.status === "EM_TRANSITO").length,
    destino: cargas.filter((c) => c.status === "NO_CLIENTE").length,
    devolvidas: devolvidas.length,
  }), [cargas, devolvidas]);

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white" data-testid="cliente-portal">
      <header className="border-b border-border bg-[#14151A] px-6 py-4 flex items-center gap-4 flex-wrap">
        <div className="w-10 h-10 bg-[#00E5FF] flex items-center justify-center">
          <PackageSearchIcon className="w-6 h-6 text-black" strokeWidth={2.5} />
        </div>
        <div>
          <div className="font-display text-xl font-black leading-none">PORTAL DO <span className="text-[#00E5FF]">CLIENTE</span></div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">CHEM·WMS · Consulta de Cargas</div>
        </div>
        <div className="ml-auto flex items-center gap-4">
          <div className="text-right">
            <div className="text-sm font-bold" data-testid="cliente-nome">{user?.name}</div>
            <div className="text-[10px] text-muted-foreground font-mono">{me?.cnpj || ""}</div>
          </div>
          <Button variant="outline" onClick={logout} data-testid="cliente-logout" className="rounded-none border-border hover:border-[#FF3B30] hover:text-[#FF3B30] h-9 text-xs uppercase font-bold">
            <LogOutIcon className="w-3.5 h-3.5 mr-1" /> Sair
          </Button>
        </div>
      </header>

      <main className="p-6 space-y-5">
        <div className="grid grid-cols-3 gap-3 max-w-2xl">
          {[
            ["Em Trânsito p/ Você", kpis.transito, "#00E5FF"],
            ["No Seu Destino", kpis.destino, "#FFD60A"],
            ["Vazios Devolvidos", kpis.devolvidas, "#34C759"],
          ].map(([label, val, cor]) => (
            <Card key={label} className="bg-[#14151A] border-border rounded-none p-3" data-testid={`cli-kpi-${label}`}>
              <div className="font-mono text-3xl font-black" style={{ color: cor }}>{val}</div>
              <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
            </Card>
          ))}
        </div>

        <div className="flex border border-border w-fit">
          <button onClick={() => setAba("cargas")} data-testid="cli-tab-cargas"
                  className={`px-5 h-11 text-xs font-bold uppercase tracking-wider flex items-center gap-2 ${aba === "cargas" ? "bg-[#00E5FF] text-black" : "bg-[#14151A] text-muted-foreground hover:text-white"}`}>
            <TruckIcon className="w-4 h-4" /> Minhas Cargas ({cargas.length})
          </button>
          <button onClick={() => setAba("devolvidas")} data-testid="cli-tab-devolvidas"
                  className={`px-5 h-11 text-xs font-bold uppercase tracking-wider flex items-center gap-2 ${aba === "devolvidas" ? "bg-[#34C759] text-black" : "bg-[#14151A] text-muted-foreground hover:text-white"}`}>
            <PackageOpenIcon className="w-4 h-4" /> Embalagens Vazias Devolvidas ({devolvidas.length})
          </button>
        </div>

        {aba === "cargas" && (
          <Card className="bg-[#14151A] border-border rounded-none overflow-x-auto" data-testid="cli-tabela-cargas">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[9px] uppercase tracking-widest text-muted-foreground border-b border-border">
                  <th className="px-3 py-2">Contentor</th>
                  <th className="px-3 py-2">Produto</th>
                  <th className="px-3 py-2">Lote</th>
                  <th className="px-3 py-2">Qtd (L)</th>
                  <th className="px-3 py-2">NF</th>
                  <th className="px-3 py-2">Pedido</th>
                  <th className="px-3 py-2">Motorista / Placa</th>
                  <th className="px-3 py-2">Saída</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Última Posição GPS</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={10} className="px-4 py-10 text-center text-muted-foreground animate-pulse">Carregando...</td></tr>}
                {!loading && cargas.length === 0 && (
                  <tr><td colSpan={10} className="px-4 py-10 text-center text-muted-foreground">Nenhuma carga em curso para a sua empresa.</td></tr>
                )}
                {cargas.map((c) => (
                  <tr key={c.no_serie + (c.data_saida || "")} className="border-b border-border/50" data-testid={`cli-carga-${c.no_serie}`}>
                    <td className="px-3 py-2 font-mono font-bold text-[#00E5FF]">{c.no_serie}</td>
                    <td className="px-3 py-2">{c.nome_produto || "—"}</td>
                    <td className="px-3 py-2 font-mono">{c.no_lote || "—"}</td>
                    <td className="px-3 py-2 font-mono">{c.qtd_envasado?.toLocaleString("pt-BR") || "—"}</td>
                    <td className="px-3 py-2 font-mono">{c.nf_produto || "—"}</td>
                    <td className="px-3 py-2 font-mono">{c.no_pedido || "—"}</td>
                    <td className="px-3 py-2">{c.motorista || "—"} · <span className="font-mono">{c.placa || "—"}</span></td>
                    <td className="px-3 py-2 font-mono">{fmtD(c.data_saida)}</td>
                    <td className="px-3 py-2">
                      <Badge className={`rounded-none border text-[9px] font-bold ${STATUS_CORES[c.status] || "bg-muted/20 text-muted-foreground border-border"}`}>
                        {STATUS_LABEL[c.status] || c.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      {c.gps ? (
                        <a href={`https://www.google.com/maps?q=${c.gps.lat},${c.gps.lng}`} target="_blank" rel="noreferrer"
                           className="text-[#34C759] hover:underline flex items-center gap-1 font-mono text-[10px]" data-testid={`cli-gps-${c.no_serie}`}>
                          <MapPinIcon className="w-3 h-3" /> {fmtDT(c.gps.timestamp)} · ver mapa
                        </a>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        {aba === "devolvidas" && (
          <Card className="bg-[#14151A] border-border rounded-none overflow-x-auto" data-testid="cli-tabela-devolvidas">
            <div className="p-3 border-b border-border text-[10px] text-muted-foreground">
              Embalagens vazias da sua empresa que já retornaram à base (coletadas na plataforma/porto ou trazidas pelo seu caminhão).
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[9px] uppercase tracking-widest text-muted-foreground border-b border-border">
                  <th className="px-3 py-2">Contentor</th>
                  <th className="px-3 py-2">Produto (última carga)</th>
                  <th className="px-3 py-2">Lote</th>
                  <th className="px-3 py-2">NF</th>
                  <th className="px-3 py-2">Pedido</th>
                  <th className="px-3 py-2">Saída</th>
                  <th className="px-3 py-2">Retorno à Base</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {!loading && devolvidas.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">Nenhuma embalagem vazia devolvida ainda.</td></tr>
                )}
                {devolvidas.map((c) => (
                  <tr key={c.no_serie + (c.data_retorno || "")} className="border-b border-border/50" data-testid={`cli-dev-${c.no_serie}`}>
                    <td className="px-3 py-2 font-mono font-bold text-[#34C759]">{c.no_serie}</td>
                    <td className="px-3 py-2">{c.nome_produto || "—"}</td>
                    <td className="px-3 py-2 font-mono">{c.no_lote || "—"}</td>
                    <td className="px-3 py-2 font-mono">{c.nf_produto || "—"}</td>
                    <td className="px-3 py-2 font-mono">{c.no_pedido || "—"}</td>
                    <td className="px-3 py-2 font-mono">{fmtD(c.data_saida)}</td>
                    <td className="px-3 py-2 font-mono text-[#34C759]">{fmtDT(c.data_retorno)}</td>
                    <td className="px-3 py-2">
                      <Badge className="rounded-none border text-[9px] font-bold bg-[#34C759]/10 text-[#34C759] border-[#34C759]/40">DEVOLVIDO</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </main>
    </div>
  );
}
